import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Users, Search, Eye, Clock, Calendar, Camera, Radio,
  RefreshCw, AlertCircle, TrendingUp, ChevronRight,
  User, Activity, Filter, Smartphone, Mail, Phone, 
  CreditCard, Shield, StickyNote, Send, Loader2,
  UserCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { toast } from 'sonner';
import api from '@/lib/api';
import { UserDevices } from '@/components/UserDevices';

// Format duration in hours and minutes
const formatDuration = (minutes) => {
  if (!minutes || minutes === 0) return '0m';
  const hrs = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hrs === 0) return `${mins}m`;
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h ${mins}m`;
};

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Format relative time
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  return `${Math.floor(diffMins / 1440)}d ago`;
};

// Colors for pie chart
const CHART_COLORS = ['#F7931E', '#FF6B35', '#FFB347', '#FFCC5C', '#FFE066', '#98D8C8'];

// User Profile Card Component
const UserProfileCard = ({ userProfile, isLoading }) => {
  if (isLoading) {
    return (
      <Card className="border-border bg-card">
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-6 w-48 bg-muted rounded" />
            <div className="grid grid-cols-2 gap-4">
              {[1,2,3,4].map(i => <div key={i} className="h-12 bg-muted rounded" />)}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!userProfile) return null;

  const tierColors = {
    engineer: 'border-green-500 text-green-400 bg-green-500/10',
    conductor: 'border-blue-500 text-blue-400 bg-blue-500/10',
    fireman: 'border-orange-500 text-orange-400 bg-orange-500/10',
  };

  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <UserCircle className="h-5 w-5 text-primary" />
          User Profile
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Name and Username */}
        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
            <User className="h-7 w-7 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white">
              {userProfile.display_name || userProfile.username}
            </h3>
            <p className="text-sm text-muted-foreground">@{userProfile.username}</p>
            {userProfile.membership_tier && (
              <Badge className={`mt-1 capitalize ${tierColors[userProfile.membership_tier] || ''}`}>
                {userProfile.membership_tier}
              </Badge>
            )}
          </div>
          {userProfile.is_admin && (
            <Badge variant="outline" className="border-red-500 text-red-400">
              <Shield className="mr-1 h-3 w-3" />
              Admin
            </Badge>
          )}
        </div>

        {/* Contact Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
          {userProfile.email && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="text-sm font-medium truncate">{userProfile.email}</p>
              </div>
            </div>
          )}
          {userProfile.phone && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Phone</p>
                <p className="text-sm font-medium">{userProfile.phone}</p>
              </div>
            </div>
          )}
        </div>

        {/* Limits Info */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2">
          <div className="p-3 rounded-lg bg-secondary/30 text-center">
            <p className="text-xs text-muted-foreground">Device Limit</p>
            <p className="text-lg font-bold text-white">{userProfile.effective_device_limit || '—'}</p>
            <p className="text-xs text-muted-foreground">
              {userProfile.current_device_count || 0} used
            </p>
          </div>
          <div className="p-3 rounded-lg bg-secondary/30 text-center">
            <p className="text-xs text-muted-foreground">Concurrent</p>
            <p className="text-lg font-bold text-white">{userProfile.effective_concurrent_limit || '—'}</p>
            <p className="text-xs text-muted-foreground">
              {userProfile.current_active_sessions || 0} active
            </p>
          </div>
          <div className="p-3 rounded-lg bg-secondary/30 text-center">
            <p className="text-xs text-muted-foreground">Last Login</p>
            <p className="text-sm font-medium text-white">
              {userProfile.last_login ? formatDate(userProfile.last_login) : '—'}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-secondary/30 text-center">
            <p className="text-xs text-muted-foreground">Bypasses</p>
            <div className="flex justify-center gap-1 mt-1">
              {userProfile.bypass_device_limit && (
                <Badge variant="outline" className="text-xs">Device</Badge>
              )}
              {userProfile.bypass_concurrent_limit && (
                <Badge variant="outline" className="text-xs">Stream</Badge>
              )}
              {!userProfile.bypass_device_limit && !userProfile.bypass_concurrent_limit && (
                <span className="text-muted-foreground text-sm">None</span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// User Notes Component
const UserNotesSection = ({ username }) => {
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [isSending, setIsSending] = useState(false);

  const fetchNotes = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getUserNotes(username);
      setNotes(data.notes || []);
    } catch (err) {
      console.error('Failed to fetch notes:', err);
      toast.error('Failed to load notes');
    } finally {
      setIsLoading(false);
    }
  }, [username]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setIsSending(true);
    try {
      await api.addUserNote(username, newNote.trim());
      toast.success('Note added');
      setNewNote('');
      fetchNotes();
    } catch (err) {
      toast.error(err.message || 'Failed to add note');
    } finally {
      setIsSending(false);
    }
  };

  const formatNoteDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <StickyNote className="h-5 w-5 text-primary" />
          Admin Notes
        </CardTitle>
        <CardDescription>Internal notes about this user (visible only to admins)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add Note Form */}
        <div className="flex gap-2">
          <Textarea
            placeholder="Add a note about this user..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            className="min-h-[80px] resize-none"
            data-testid="add-note-textarea"
          />
          <Button 
            onClick={handleAddNote} 
            disabled={!newNote.trim() || isSending}
            className="self-end"
            data-testid="add-note-btn"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Notes List */}
        {isLoading ? (
          <div className="space-y-3">
            {[1,2].map(i => (
              <div key={i} className="animate-pulse h-20 bg-muted rounded-lg" />
            ))}
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <StickyNote className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>No notes yet</p>
            <p className="text-sm">Add a note above to keep track of important information</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {notes.map((note, idx) => (
              <div 
                key={note._id || note.id || idx} 
                className="p-3 rounded-lg bg-secondary/30 border border-border/50"
              >
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline" className="text-xs">
                    {note.admin_user || note.created_by || 'Admin'}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatNoteDate(note.created_at || note.timestamp)}
                  </span>
                </div>
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {note.content || note.note || note.text}
                </p>
                {note.source && (
                  <Badge variant="secondary" className="mt-2 text-xs">
                    {note.source}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// User Activity Detail View
const UserActivityDetail = ({ username, sessions, onBack }) => {
  const [cameraLookup, setCameraLookup] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  
  // Fetch user profile from aMember
  useEffect(() => {
    const fetchUserProfile = async () => {
      setIsLoadingProfile(true);
      try {
        const data = await api.getUser(username);
        setUserProfile(data);
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
      } finally {
        setIsLoadingProfile(false);
      }
    };
    fetchUserProfile();
  }, [username]);
  
  // Fetch cameras to get short_codes
  useEffect(() => {
    const fetchCameras = async () => {
      try {
        const response = await api.getCameras();
        // Handle both array and {cameras: [...]} response formats
        const cameras = Array.isArray(response) ? response : (response?.cameras || []);
        const lookup = {};
        cameras.forEach(cam => {
          if (cam._id) {
            lookup[cam._id] = cam.short_code || '';
          }
        });
        console.log('Camera lookup built:', Object.keys(lookup).length, 'cameras');
        setCameraLookup(lookup);
      } catch (err) {
        console.error('Failed to fetch cameras for short_code lookup:', err);
      }
    };
    fetchCameras();
  }, []);
  
  // Helper to get short code - prefer camera's short_code, then session's, then ID fallback
  const getShortCode = useCallback((session) => {
    const cameraId = session.camera_id || '';
    // First try camera's short_code from lookup
    if (cameraId && cameraLookup[cameraId]) {
      return cameraLookup[cameraId];
    }
    // Then try session's camera_short_code
    if (session.camera_short_code) {
      return session.camera_short_code;
    }
    // Fallback to last 6 chars of ID
    return cameraId ? cameraId.slice(-6).toUpperCase() : '';
  }, [cameraLookup]);
  
  // Calculate stats
  const totalWatchMinutes = sessions.reduce((sum, s) => {
    return sum + (s.duration_seconds || 0) / 60;
  }, 0);
  
  const cameraSessions = sessions.filter(s => s.session_type !== 'radio');
  const radioSessions = sessions.filter(s => s.session_type === 'radio');
  
  // Group by camera/station for pie chart - recompute when cameraLookup changes
  const topCameras = useMemo(() => {
    const byCamera = {};
    sessions.forEach(s => {
      const name = s.camera_name || s.station_name || 'Unknown Camera';
      const id = s.camera_id || s.station_id || s.stream_id || '';
      const cameraId = s.camera_id || '';
      // Get short code inline to avoid stale closure
      let shortCode = '';
      if (cameraId && cameraLookup[cameraId]) {
        shortCode = cameraLookup[cameraId];
      } else if (s.camera_short_code) {
        shortCode = s.camera_short_code;
      } else if (cameraId) {
        shortCode = cameraId.slice(-6).toUpperCase();
      }
      const key = id || name;
      
      if (!byCamera[key]) {
        byCamera[key] = { name, shortCode, minutes: 0, count: 0 };
      }
      byCamera[key].minutes += (s.duration_seconds || 0) / 60;
      byCamera[key].count += 1;
    });
    
    return Object.values(byCamera)
      .sort((a, b) => b.minutes - a.minutes)
      .slice(0, 6);
  }, [sessions, cameraLookup]);

  // Display name from profile or fallback to username
  const displayName = userProfile?.display_name || username;

  return (
    <div className="space-y-6">
      {/* Back button and header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ChevronRight className="mr-1 h-4 w-4 rotate-180" />
          Back to Users
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <User className="h-6 w-6 text-primary" />
            {displayName}
          </h2>
          <p className="text-sm text-muted-foreground">
            {displayName !== username && <span className="mr-2">@{username}</span>}
            User activity, devices, and watch history
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="grid w-full max-w-lg grid-cols-4">
          <TabsTrigger value="profile" data-testid="tab-profile">
            <UserCircle className="mr-2 h-4 w-4" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">
            <Activity className="mr-2 h-4 w-4" />
            Activity
          </TabsTrigger>
          <TabsTrigger value="devices" data-testid="tab-devices">
            <Smartphone className="mr-2 h-4 w-4" />
            Devices
          </TabsTrigger>
          <TabsTrigger value="notes" data-testid="tab-notes">
            <StickyNote className="mr-2 h-4 w-4" />
            Notes
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6">
          <UserProfileCard userProfile={userProfile} isLoading={isLoadingProfile} />
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            <Card className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                    <Clock className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatDuration(totalWatchMinutes)}</p>
                    <p className="text-xs text-muted-foreground">Total Watch Time</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/10">
                    <Activity className="h-5 w-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{sessions.length}</p>
                    <p className="text-xs text-muted-foreground">Total Sessions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10">
                    <Camera className="h-5 w-5 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{cameraSessions.length}</p>
                    <p className="text-xs text-muted-foreground">Camera Sessions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border bg-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-violet-500/10">
                    <Radio className="h-5 w-5 text-violet-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{radioSessions.length}</p>
                    <p className="text-xs text-muted-foreground">Radio Sessions</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Top Cameras Pie Chart */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-base">Watch Time by Camera</CardTitle>
          </CardHeader>
          <CardContent>
            {topCameras.length === 0 ? (
              <div className="flex h-48 items-center justify-center text-muted-foreground">
                No watch data available
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <ResponsiveContainer width="50%" height={200}>
                  <PieChart>
                    <Pie
                      data={topCameras}
                      dataKey="minutes"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                    >
                      {topCameras.map((entry, index) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatDuration(value)}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                        color: 'white'
                      }}
                      labelStyle={{ color: 'white' }}
                      itemStyle={{ color: 'white' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {topCameras.map((cam, i) => (
                    <div key={`${cam.name}-${cam.shortCode}`} className="flex items-center gap-2 text-sm">
                      <div 
                        className="h-3 w-3 rounded-full shrink-0" 
                        style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                      <span className="flex-1 truncate" title={cam.shortCode ? `${cam.name} (${cam.shortCode})` : cam.name}>
                        {cam.name}
                        {cam.shortCode && (
                          <span className="text-muted-foreground ml-1 text-xs font-mono">({cam.shortCode})</span>
                        )}
                      </span>
                      <span className="text-muted-foreground shrink-0">{formatDuration(cam.minutes)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[200px] overflow-y-auto">
              {sessions.slice(0, 8).map((session, i) => {
                const shortCode = getShortCode(session);
                return (
                  <div key={session._id || i} className="flex items-center gap-3 text-sm">
                    {session.session_type === 'radio' ? (
                      <Radio className="h-4 w-4 text-violet-500 shrink-0" />
                    ) : (
                      <Camera className="h-4 w-4 text-amber-500 shrink-0" />
                    )}
                    <span className="flex-1 truncate">
                      {session.camera_name || session.station_name || 'Unknown Camera'}
                      {shortCode && (
                        <span className="text-muted-foreground ml-1 text-xs font-mono">({shortCode})</span>
                      )}
                    </span>
                    <span className="text-muted-foreground shrink-0">
                      {formatDuration((session.duration_seconds || 0) / 60)}
                    </span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {formatRelativeTime(session.started_at)}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Full Session History Table */}
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="text-base">Session History</CardTitle>
          <CardDescription>All viewing sessions for this user</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Camera/Station</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Duration</TableHead>
                <TableHead className="text-center">Quality</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((session, i) => {
                // Calculate health badge
                const bufferCount = session.qoe_buffer_count || 0;
                const errorCount = session.qoe_error_count || 0;
                let healthBadge = null;
                
                if (session.qoe_buffer_count != null || session.qoe_error_count != null) {
                  if (errorCount > 3 || bufferCount > 20) {
                    healthBadge = <Badge className="bg-red-500/20 text-red-400 text-xs">Poor</Badge>;
                  } else if (errorCount > 0 || bufferCount > 5) {
                    healthBadge = <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">Fair</Badge>;
                  } else {
                    healthBadge = <Badge className="bg-green-500/20 text-green-400 text-xs">Good</Badge>;
                  }
                }
                
                const shortCode = getShortCode(session);
                
                // Infer session status - if started_at + duration is in the past, it's ended
                let displayStatus = session.state || 'ended';
                let isActive = false;
                if (session.started_at && session.duration_seconds) {
                  const startTime = new Date(session.started_at).getTime();
                  const endTime = startTime + (session.duration_seconds * 1000);
                  const now = Date.now();
                  // If session "ended" more than 5 minutes ago, mark as ended
                  if (now > endTime + (5 * 60 * 1000)) {
                    displayStatus = 'ended';
                  } else if (session.state === 'playing') {
                    isActive = true;
                  }
                }
                
                return (
                  <TableRow key={session._id || i}>
                    <TableCell className="font-medium">
                      {session.camera_name || session.station_name || 'Unknown Camera'}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {shortCode || '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {session.session_type || 'camera'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(session.started_at)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatDuration((session.duration_seconds || 0) / 60)}
                    </TableCell>
                    <TableCell className="text-center">
                      {healthBadge || <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={isActive ? 'default' : 'secondary'}
                        className={isActive ? 'bg-green-500/20 text-green-400' : ''}
                      >
                        {displayStatus}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="devices" className="mt-6">
          <UserDevices username={username} />
        </TabsContent>

        {/* Notes Tab */}
        <TabsContent value="notes" className="mt-6">
          <UserNotesSection username={username} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Main Users Page Component
export function UsersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [users, setUsers] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserSessions, setSelectedUserSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');
  const [userProfiles, setUserProfiles] = useState({}); // Cache for user display names

  // Fetch user profile for display name - use ref to avoid dependency issues
  const userProfilesRef = React.useRef(userProfiles);
  userProfilesRef.current = userProfiles;

  const fetchUserProfile = useCallback(async (username) => {
    if (userProfilesRef.current[username]) return; // Already cached
    try {
      const profile = await api.getUser(username);
      setUserProfiles(prev => ({
        ...prev,
        [username]: profile
      }));
    } catch (err) {
      // Silently fail - will just show username
      console.error(`Failed to fetch profile for ${username}:`, err);
    }
  }, []); // No dependencies - uses ref

  // Fetch sessions and extract unique users
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Fetch session history to get unique users
      const data = await api.getSessionHistory(500, 0);
      const sessions = data.sessions || [];
      setAllSessions(sessions);
      
      // Extract unique users with their stats
      const userMap = {};
      sessions.forEach(session => {
        const username = session.username;
        if (!username) return; // Skip anonymous
        
        if (!userMap[username]) {
          userMap[username] = {
            username,
            displayName: session.user_display_name || null, // Use from session if available
            totalSessions: 0,
            totalWatchMinutes: 0,
            lastSeen: null,
            cameras: new Set(),
            isActive: false
          };
        }
        
        // Update display name if available from session
        if (session.user_display_name && !userMap[username].displayName) {
          userMap[username].displayName = session.user_display_name;
        }
        
        userMap[username].totalSessions += 1;
        userMap[username].totalWatchMinutes += (session.duration_seconds || 0) / 60;
        
        if (session.camera_name) {
          userMap[username].cameras.add(session.camera_name);
        }
        
        // Track last seen
        if (!userMap[username].lastSeen || new Date(session.started_at) > new Date(userMap[username].lastSeen)) {
          userMap[username].lastSeen = session.started_at;
        }
        
        // Check if currently active
        if (session.state === 'playing' || session.state === 'paused') {
          userMap[username].isActive = true;
        }
      });
      
      // Convert to array and sort by watch time
      const userList = Object.values(userMap)
        .map(u => ({ ...u, cameras: u.cameras.size }))
        .sort((a, b) => b.totalWatchMinutes - a.totalWatchMinutes);
      
      setUsers(userList);
      
      // Fetch profiles for top users to get display names (batch of 10)
      const topUsernames = userList.slice(0, 10).map(u => u.username);
      topUsernames.forEach(username => {
        if (!userProfilesRef.current[username]) {
          fetchUserProfile(username);
        }
      });
      
    } catch (err) {
      setError(err.message || 'Failed to load user data');
    } finally {
      setIsLoading(false);
    }
  }, [fetchUserProfile]); // Only depends on fetchUserProfile which is stable

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle user selection
  const handleSelectUser = (username) => {
    const userSessions = allSessions
      .filter(s => s.username === username)
      .sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
    
    setSelectedUser(username);
    setSelectedUserSessions(userSessions);
    setSearchParams({ user: username });
  };

  // Filter users by search (search both username and display name)
  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const displayName = userProfiles[u.username]?.display_name || u.displayName || '';
    return u.username.toLowerCase().includes(query) || 
           displayName.toLowerCase().includes(query);
  });

  // Helper to get display name for a user
  const getDisplayName = (username) => {
    return userProfiles[username]?.display_name || 
           users.find(u => u.username === username)?.displayName || 
           null;
  };

  // Quick user lookup by email/phone/username
  const [lookupQuery, setLookupQuery] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);

  const handleQuickLookup = async () => {
    if (!lookupQuery.trim()) return;
    setIsLookingUp(true);
    try {
      // The API accepts username or email
      const userData = await api.getUser(lookupQuery.trim());
      if (userData && userData.username) {
        // Found user - navigate to their detail page
        toast.success(`Found user: ${userData.display_name || userData.username}`);
        handleSelectUser(userData.username);
      }
    } catch (err) {
      toast.error(err.message || 'User not found');
    } finally {
      setIsLookingUp(false);
    }
  };

  // Show user detail view
  if (selectedUser) {
    return (
      <div className="space-y-6" data-testid="user-activity-page">
        <UserActivityDetail 
          username={selectedUser}
          sessions={selectedUserSessions}
          onBack={() => {
            setSelectedUser(null);
            setSearchParams({});
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-muted-foreground">View user activity and watch history</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-36">
              <Calendar className="mr-2 h-4 w-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchData}
            disabled={isLoading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Search and Quick Lookup */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Regular search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="search-users-input"
              />
            </div>
            
            {/* Quick Lookup */}
            <div className="flex gap-2">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Lookup by email or username..."
                  value={lookupQuery}
                  onChange={(e) => setLookupQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleQuickLookup()}
                  className="pl-9 w-64"
                  data-testid="quick-lookup-input"
                />
              </div>
              <Button 
                onClick={handleQuickLookup}
                disabled={!lookupQuery.trim() || isLookingUp}
                data-testid="quick-lookup-btn"
              >
                {isLookingUp ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Search className="mr-2 h-4 w-4" />
                    Lookup
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="flex items-center gap-4 p-4">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <div className="flex-1">
              <p className="font-medium">Failed to load users</p>
              <p className="text-sm text-muted-foreground">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading ? (
        <Card className="border-border bg-card">
          <CardContent className="p-6">
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-10 w-10 animate-pulse rounded-full bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 animate-pulse rounded bg-muted" />
                    <div className="h-3 w-48 animate-pulse rounded bg-muted" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Users List */
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Active Users
              <Badge variant="secondary" className="ml-2">{filteredUsers.length}</Badge>
            </CardTitle>
            <CardDescription>
              Click on a user to see their detailed watch history
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="mb-4 h-12 w-12 text-muted-foreground/30" />
                <p className="font-medium">No users found</p>
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? 'Try a different search term' : 'No registered users have watch history yet'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Watch Time</TableHead>
                    <TableHead className="text-right">Sessions</TableHead>
                    <TableHead className="text-right">Cameras</TableHead>
                    <TableHead>Last Seen</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => {
                    const displayName = getDisplayName(user.username);
                    return (
                      <TableRow 
                        key={user.username}
                        className="cursor-pointer hover:bg-secondary/50"
                        onClick={() => handleSelectUser(user.username)}
                        data-testid={`user-row-${user.username}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                              <User className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-white">
                                {displayName || user.username}
                              </p>
                              {displayName && (
                                <p className="text-xs text-muted-foreground">@{user.username}</p>
                              )}
                              {user.isActive && (
                                <Badge variant="default" className="text-xs bg-green-500/20 text-green-400 mt-0.5">
                                  Active Now
                                </Badge>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatDuration(user.totalWatchMinutes)}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.totalSessions}
                        </TableCell>
                        <TableCell className="text-right">
                          {user.cameras}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatRelativeTime(user.lastSeen)}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default UsersPage;
